"use client";

import { useState } from "react";
import Link from "next/link";
import { signIn } from "next-auth/react";
import { useRouter, useSearchParams } from "next/navigation";
import { useToast } from "@/app/_lib/hooks/use-toast";
import { requestPasswordResetAction } from "@/app/_lib/actions/password-reset.actions";

export default function SignInPage() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const toast = useToast();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [needsPasswordReset, setNeedsPasswordReset] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const callbackUrl = searchParams.get("callbackUrl") || "/projects";

  const handlePasswordReset = async () => {
    if (!email) {
      toast({
        message: "Please enter your email address first",
        type: "error",
        state: "active",
      });
      return;
    }

    setIsLoading(true);
    const [resetError, resetResult] = await requestPasswordResetAction({
      email,
    });

    if (!resetError && resetResult?.success) {
      setNeedsPasswordReset(true);
      toast({
        message:
          "We've sent a password reset email to your address. Please check your inbox.",
        type: "info",
        state: "active",
      });
    } else {
      toast({
        message:
          resetError?.message ||
          "Unable to send password reset email. Please check your email address.",
        type: "error",
        state: "active",
      });
    }
    setIsLoading(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setIsLoading(true);

    try {
      const result = await signIn("credentials", {
        email,
        password,
        redirect: false,
      });

      if (result?.error) {
        // Check if this is a rate limit error (contains "too many" or "attempts")
        const isRateLimitError =
          result.error.toLowerCase().includes("too many") ||
          result.error.toLowerCase().includes("attempt");

        if (isRateLimitError) {
          // Show rate limit error message directly
          setError(result.error);
          setIsLoading(false);
        } else {
          // Regular authentication error
          setError("Invalid email or password");
          setIsLoading(false);
        }
      } else {
        // Successful login - redirect
        toast({
          message: "Sign in successful! Redirecting...",
          type: "success",
          state: "active",
        });
        router.push(callbackUrl);
        router.refresh();
      }
    } catch (err) {
      setError("An error occurred. Please try again.");
      setIsLoading(false);
    }
  };

  // If user needs password reset (migrated user), show reset notice
  if (needsPasswordReset) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-base-100 text-base-content">
        <div className="w-full max-w-md">
          <div className="bg-base-100 p-8 rounded-md">
            <h1 className="text-2xl font-bold text-center mb-4">
              Check Your Email
            </h1>
            <p className="text-center mb-4">
              We&apos;ve sent a password reset email to{" "}
              <span className="font-semibold">{email}</span>.
            </p>

            <div className="mb-4 p-4 bg-info/10 border border-info/40 rounded-md">
              <div className="text-sm">
                Please check your inbox for a link to reset your password. The
                link will be valid for 24 hours.
              </div>
            </div>

            <button
              onClick={() => {
                setNeedsPasswordReset(false);
                setPassword("");
              }}
              className="w-full p-4 rounded-md border border-base-300 hover:bg-base-200 font-medium"
            >
              Back to Sign In
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-base-100 text-base-content">
      <div className="w-full max-w-md">
        <div className="bg-base-100 p-8 rounded-md">
          <h1 className="text-2xl font-bold text-center mb-6">Sign In</h1>

          {error && (
            <div className="mb-4 p-3 bg-error/10 border border-error/40 text-error rounded-md">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label
                htmlFor="email"
                className="block text-base font-medium mb-3"
              >
                Email
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="w-full text-base bg-base-200 p-4 rounded-md border-base-300 border text-base-content placeholder:text-base-content/50 focus:outline-none focus:ring-2 focus:ring-cnd-purple"
                placeholder="your@email.com"
                disabled={isLoading}
              />
            </div>

            <div>
              <label
                htmlFor="password"
                className="block text-base font-medium mb-3"
              >
                Password
              </label>
              <div className="relative">
                <input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="w-full text-base bg-base-200 p-4 pr-12 rounded-md border-base-300 border text-base-content placeholder:text-base-content/50 focus:outline-none focus:ring-2 focus:ring-cnd-purple"
                  placeholder="••••••••"
                  disabled={isLoading}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-base-content/60 hover:text-base-content"
                >
                  {showPassword ? "Hide" : "Show"}
                </button>
              </div>
              <div className="mt-2 text-right">
                <button
                  type="button"
                  onClick={handlePasswordReset}
                  disabled={isLoading}
                  className="text-sm font-medium text-primary hover:opacity-80 disabled:opacity-50"
                >
                  Reset password
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="w-full bg-primary hover:bg-primary/90 border border-primary text-primary-content p-4 rounded-md text-base font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isLoading ? "Signing in..." : "Sign In"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
